import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { baseUrl, formatDateTime } from "../Univ.js";

const NO_ROOM = "Unassigned";
const NO_TYPE = "Other";

const ROOM_TYPE_KEYS = [
    "room_no__room__room",
    "room_no__room_type",
    "room_no__type",
    "room_no__category",
    "room_no__room_category",
    "room_no__roomtype",
    "room_no__ward",
    "room_type",
];

const clean = (value, fallback = "-") => {
    const text = String(value ?? "").trim();
    return text || fallback;
};

const getRoom = (patient) =>
    clean(patient?.room_no__room_no, NO_ROOM);

const getType = (patient) => {
    for (const key of ROOM_TYPE_KEYS) {
        const value = patient?.[key];

        if (
            value !== undefined &&
            value !== null &&
            String(value).trim() !== ""
        ) {
            return String(value).trim();
        }
    }

    const key = Object.keys(patient || {}).find(
        (field) =>
            /^room_no__.*(type|categ|class|ward)/i.test(field) &&
            String(patient[field] ?? "").trim() !== ""
    );

    return key ? String(patient[key]).trim() : NO_TYPE;
};

const compareNatural = (a, b, lastLabel) => {
    if (a === lastLabel) return 1;
    if (b === lastLabel) return -1;

    return String(a).localeCompare(String(b), undefined, {
        numeric: true,
        sensitivity: "base",
    });
};

const typeTheme = (type) => {
    const value = String(type).toLowerCase();

    // Check NON-AC before AC.
    if (/non[\s-]*a\s*\/?\s*c|non[\s-]*ac/.test(value)) {
        return {
            color: "#c2410c",
            bg: "#fff7ed",
            border: "#fed7aa",
            icon: "fa-fan",
        };
    }

    if (/icu|critical|nicu/.test(value)) {
        return {
            color: "#b91c1c",
            bg: "#fef2f2",
            border: "#fecaca",
            icon: "fa-heart-pulse",
        };
    }

    if (/\ba\s*\/?\s*c\b|air[\s-]*condition/.test(value)) {
        return {
            color: "#0369a1",
            bg: "#eff8ff",
            border: "#bae6fd",
            icon: "fa-snowflake",
        };
    }

    if (/shar|general|ward|dorm/.test(value)) {
        return {
            color: "#6d28d9",
            bg: "#f5f3ff",
            border: "#ddd6fe",
            icon: "fa-people-roof",
        };
    }

    if (/delux|vip|suite|special|private|single/.test(value)) {
        return {
            color: "#a16207",
            bg: "#fffbeb",
            border: "#fde68a",
            icon: "fa-crown",
        };
    }

    return {
        color: "#334155",
        bg: "#f8fafc",
        border: "#cbd5e1",
        icon: "fa-bed",
    };
};

function IpViewList() {
    const [ipViewData, setIpViewData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchText, setSearchText] = useState("");
    const [selectedType, setSelectedType] = useState("ALL");
    const [selectedRoom, setSelectedRoom] = useState("ALL");

    useEffect(() => {
        fetchIpViewData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchIpViewData = async () => {
        try {
            setLoading(true);
            setError("");

            const response = await axios.get(
                `${baseUrl}view-ip/?status=2`
            );

            const data = Array.isArray(response.data)
                ? response.data
                : Array.isArray(response.data?.results)
                    ? response.data.results
                    : [];

            setIpViewData(data);
        } catch (err) {
            console.error("Error fetching inpatient consultant data:", err);
            setError("Unable to load inpatient details. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Room type counts.
    const typeOptions = useMemo(() => {
        const counts = {};

        ipViewData.forEach((patient) => {
            const type = getType(patient);
            counts[type] = (counts[type] || 0) + 1;
        });

        return Object.entries(counts).sort(([a], [b]) =>
            compareNatural(a, b, NO_TYPE)
        );
    }, [ipViewData]);

    // Room options for the selected type.
    const roomOptions = useMemo(() => {
        const rooms = new Set();

        ipViewData.forEach((patient) => {
            const type = getType(patient);

            if (selectedType === "ALL" || type === selectedType) {
                rooms.add(getRoom(patient));
            }
        });

        return [...rooms].sort((a, b) =>
            compareNatural(a, b, NO_ROOM)
        );
    }, [ipViewData, selectedType]);

    const handleTypeChange = (value) => {
        setSelectedType(value);
        setSelectedRoom("ALL");
    };

    // Group patients by room type and room.
    const groupedTypes = useMemo(() => {
        const search = searchText.trim().toLowerCase();
        const tree = {};

        ipViewData.forEach((patient) => {
            const type = getType(patient);
            const room = getRoom(patient);

            if (selectedType !== "ALL" && type !== selectedType) {
                return;
            }

            if (selectedRoom !== "ALL" && room !== selectedRoom) {
                return;
            }

            const searchable = [
                patient.ip_id,
                patient.pat__pat_id,
                patient.pat__patient,
                patient.pat__mobile,
                patient.adhar,
                patient.primary_consult__consultant,
                patient.pat__address1,
                patient.pat__gender,
                patient.payment,
                room,
                type,
            ];

            const matchesSearch =
                !search ||
                searchable.some((value) =>
                    String(value ?? "").toLowerCase().includes(search)
                );

            if (!matchesSearch) return;

            if (!tree[type]) {
                tree[type] = {};
            }

            if (!tree[type][room]) {
                tree[type][room] = [];
            }

            tree[type][room].push(patient);
        });

        return Object.entries(tree)
            .sort(([a], [b]) => compareNatural(a, b, NO_TYPE))
            .map(([type, roomsObject]) => {
                const rooms = Object.entries(roomsObject).sort(
                    ([a], [b]) => compareNatural(a, b, NO_ROOM)
                );

                const total = rooms.reduce(
                    (sum, [, patients]) => sum + patients.length,
                    0
                );

                return { type, total, rooms };
            });
    }, [ipViewData, searchText, selectedType, selectedRoom]);

    const totalPatients = ipViewData.length;

    const occupiedRooms = new Set(
        ipViewData
            .map(getRoom)
            .filter((room) => room !== NO_ROOM)
    ).size;

    const displayValue = (value) => {
        if (
            value === null ||
            value === undefined ||
            String(value).trim() === ""
        ) {
            return "-";
        }

        return value;
    };

    const displayDate = (value) => {
        if (!value) return "-";

        try {
            return formatDateTime(value) || "-";
        } catch {
            return String(value);
        }
    };

    const getProfilePicture = (value) => {
        if (!value) return "";

        const image = String(value).trim();

        if (!image) return "";

        if (/^(https?:\/\/|data:image\/)/i.test(image)) {
            return image;
        }

        return `${baseUrl.replace(/\/+$/, "")}/${image.replace(/^\/+/, "")}`;
    };

    return (
        <section className="ip-dashboard" translate="no">
            {/* Header */}
            <div className="ip-header">
                <div>
                    <h2 className="ip-heading">
                        <i className="fa-solid fa-user-doctor ip-heading-icon" />
                        <span>IP Consultant View</span>
                    </h2>

                    <p className="ip-subtitle">
                        Inpatient details grouped by room type and room number
                    </p>
                </div>

                <button
                    type="button"
                    className="ip-refresh"
                    onClick={fetchIpViewData}
                    disabled={loading}
                >
                    <i
                        className={`fa-solid ${
                            loading ? "fa-spinner fa-spin" : "fa-rotate"
                        }`}
                    />
                    {loading ? "Loading..." : "Refresh"}
                </button>
            </div>

            {/* Summary */}
            <div className="ip-summary">
                <div className="ip-summary-card">
                    <div className="ip-summary-icon patients">
                        <i className="fa-solid fa-hospital-user" />
                    </div>

                    <div>
                        <div className="ip-summary-label">
                            Total Inpatients
                        </div>
                        <div className="ip-summary-value">
                            {totalPatients}
                        </div>
                    </div>
                </div>

                <div className="ip-summary-card">
                    <div className="ip-summary-icon rooms">
                        <i className="fa-solid fa-door-open" />
                    </div>

                    <div>
                        <div className="ip-summary-label">
                            Occupied Rooms
                        </div>
                        <div className="ip-summary-value">
                            {occupiedRooms}
                        </div>
                    </div>
                </div>

                <div className="ip-summary-card">
                    <div className="ip-summary-icon types">
                        <i className="fa-solid fa-layer-group" />
                    </div>

                    <div>
                        <div className="ip-summary-label">
                            Room Types
                        </div>
                        <div className="ip-summary-value">
                            {typeOptions.length}
                        </div>
                    </div>
                </div>
            </div>

            {/* Search and filters */}
            <div className="ip-toolbar">
                <input
                    type="search"
                    className="ip-search"
                    placeholder="Search patient, IP ID, mobile, consultant, room..."
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    aria-label="Search inpatient records"
                />

                <select
                    className="ip-select"
                    value={selectedType}
                    onChange={(event) =>
                        handleTypeChange(event.target.value)
                    }
                    aria-label="Filter by room type"
                >
                    <option value="ALL">All Room Types</option>

                    {typeOptions.map(([type, count]) => (
                        <option key={type} value={type}>
                            {type} ({count})
                        </option>
                    ))}
                </select>

                <select
                    className="ip-select"
                    value={selectedRoom}
                    onChange={(event) =>
                        setSelectedRoom(event.target.value)
                    }
                    aria-label="Filter by room"
                >
                    <option value="ALL">All Rooms</option>

                    {roomOptions.map((room) => (
                        <option key={room} value={room}>
                            {room === NO_ROOM
                                ? "Unassigned Room"
                                : `Room ${room}`}
                        </option>
                    ))}
                </select>
            </div>

            {/* Loading */}
            {loading && (
                <div className="ip-state">
                    <i className="fa-solid fa-spinner fa-spin" />
                    <span style={{ marginLeft: 9 }}>
                        Loading inpatient details...
                    </span>
                </div>
            )}

            {/* Error */}
            {!loading && error && (
                <div className="ip-state ip-error">
                    <p>{error}</p>

                    <button
                        type="button"
                        className="ip-refresh"
                        onClick={fetchIpViewData}
                    >
                        <i className="fa-solid fa-rotate" />
                        Try Again
                    </button>
                </div>
            )}

            {/* Empty response */}
            {!loading && !error && ipViewData.length === 0 && (
                <div className="ip-state">
                    <i className="fa-solid fa-bed ip-state-icon" />
                    No inpatient records found.
                </div>
            )}

            {/* No filter matches */}
            {!loading &&
                !error &&
                ipViewData.length > 0 &&
                groupedTypes.length === 0 && (
                    <div className="ip-state">
                        <i className="fa-solid fa-magnifying-glass ip-state-icon" />
                        No patients match your search or selected filters.
                    </div>
                )}

            {/* Room-type sections */}
            {!loading &&
                !error &&
                groupedTypes.map(({ type, total, rooms }) => {
                    const theme = typeTheme(type);

                    // One grid per room type, with patients from all rooms.
                    const patients = rooms.flatMap(
                        ([room, roomPatients]) =>
                            roomPatients.map((patient) => ({
                                patient,
                                room,
                            }))
                    );

                    return (
                        <div className="ip-type-block" key={type}>
                            <div
                                className="ip-type-header"
                                style={{
                                    background: theme.bg,
                                    borderColor: theme.border,
                                    borderLeftColor: theme.color,
                                    color: theme.color,
                                }}
                            >
                                <i
                                    className={`fa-solid ${theme.icon} ip-type-icon`}
                                />

                                <h3 className="ip-type-title">{type}</h3>

                                <span className="ip-type-count">
                                    {total}{" "}
                                    {total === 1 ? "Patient" : "Patients"}
                                </span>
                            </div>

                            <div className="ip-beds">
                                {patients.map(({ patient, room }, index) => {
                                    const picture = getProfilePicture(
                                        patient.profile_pic
                                    );

                                    return (
                                        <div
                                            className="ip-bed-card"
                                            key={
                                                patient.id ??
                                                patient.ip_id ??
                                                `${type}-${room}-${index}`
                                            }
                                            style={{
                                                borderTopColor: theme.color,
                                            }}
                                        >
                                            <span className="ip-bed-room">
                                                {room === NO_ROOM
                                                    ? "No room assigned"
                                                    : `Room ${room}`}
                                            </span>

                                            <div className="ip-bed-top">
                                                <span
                                                    className="ip-bed-icon"
                                                    style={{
                                                        background: theme.bg,
                                                        color: theme.color,
                                                    }}
                                                >
                                                    {picture ? (
                                                        <img
                                                            src={picture}
                                                            alt={displayValue(
                                                                patient.pat__patient
                                                            )}
                                                            className="thumbnail"
                                                            onError={(event) => {
                                                                event.currentTarget.style.display =
                                                                    "none";
                                                            }}
                                                        />
                                                    ) : (
                                                        <i className="fa-solid fa-bed" />
                                                    )}
                                                </span>

                                                <div className="ip-patient-heading">
                                                    <div className="ip-patient-name">
                                                        {displayValue(
                                                            patient.pat__patient
                                                        )}
                                                    </div>

                                                    <div className="ip-patient-sub">
                                                        <Link
                                                            className="ip-id"
                                                            to={`/ip-pat-view/${patient.id}/`}
                                                        >
                                                            {displayValue(
                                                                patient.ip_id
                                                            )}
                                                        </Link>

                                                        <span>
                                                            {" "}
                                                            ·{" "}
                                                            {displayValue(
                                                                patient.pat__age
                                                            )}{" "}
                                                            yrs
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="ip-info">
                                                <i className="fa-solid fa-id-card" />
                                                <span>
                                                    {displayValue(
                                                        patient.pat__pat_id
                                                    )}
                                                </span>

                                                <i className="fa-solid fa-phone" />
                                                <span>
                                                    {displayValue(
                                                        patient.pat__mobile
                                                    )}
                                                </span>

                                                <i className="fa-solid fa-user-doctor" />
                                                <span>
                                                    {displayValue(
                                                        patient.primary_consult__consultant
                                                    )}
                                                </span>

                                                <i className="fa-solid fa-location-dot" />
                                                <span>
                                                    {displayValue(
                                                        patient.pat__address1
                                                    )}
                                                </span>

                                                <i className="fa-solid fa-calendar-check" />
                                                <span>
                                                    {displayDate(
                                                        patient.admit_date
                                                    )}
                                                </span>

                                                <i className="fa-solid fa-venus-mars" />
                                                <span>
                                                    {displayValue(
                                                        patient.pat__gender
                                                    )}
                                                </span>
                                            </div>

                                            <div className="ip-bed-footer">
                                                <span className="ip-payment">
                                                    {displayValue(
                                                        patient.payment
                                                    )}
                                                </span>

                                                <Link
                                                    className="ip-view-btn"
                                                    to={`/ip-cons-detail/${patient.id}/`}
                                                >
                                                    <i className="fa-solid fa-eye" />
                                                    View
                                                </Link>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
        </section>
    );
}

export default IpViewList;